import axios from 'axios';
import { phone as formatPhone } from 'phone'
import { MINORS, PERSON, SMS_OPTIN, SURVEY_ID, State, SELECTED_RESERVATION, PLAYER, SIGNATURE, HEARD_FROM, OCCASION, SURVEY_ANSWERS, BOOKER, GUARDIAN, WAIVER_TYPE } from './state';
import {
  UPDATE_BOOKER,
  UPDATE_MINORS,
  UPDATE_PERSON,
  UPDATE_RESERVATIONS,
  UPDATE_SELECTED_RESERVATION,
  UPDATE_SURVEYS,
  UPDATE_TAGS,
  UPDATE_WAIVER_CONTRACT,
  UPDATE_WAIVER_TYPE,
} from "./mutations";
import { getDisplayName } from '@/utils';
import { ActionContext } from 'vuex';
import { IntBoolean, Person, PlayerMinor, ReservationMinor, ReservationPerson, ReservationTag, Survey, TextPermissions, WaiverMinor, WaiverPerson } from '@/types/common';
import { GET_SURVEY_OPTIONS, IS_BOOKER } from './getters';

export const CREATE_MINORS                = 'createMinors';
export const FETCH_WAIVER_COPY            = 'fetchWaiverCopy';
export const FETCH_BOOKER                 = 'fetchBooker';
export const FETCH_GLOBAL_DATA            = 'fetchGlobalData';
export const FETCH_PEOPLE                 = 'fetchPeople';
export const FETCH_PERSON                 = 'fetchPerson';
export const FETCH_RESERVATION            = 'fetchReservation';
export const FETCH_RESERVATIONS_IN_RANGE  = 'fetchReservationsInRange';
export const FETCH_SURVEYS                = 'fetchSurveys';
export const FETCH_TAGS                   = 'fetchTags';
export const FIND_OR_CREATE_PERSON        = 'findOrCreatePerson';
export const FIND_OR_CREATE_RESERVATION   = 'findOrCreateReservation'
export const SUBMIT_KLAVIYO               = 'submitKlaviyo';
export const SUBMIT_WAIVER                = 'submitWaiver';

export default {

  [FETCH_WAIVER_COPY]: async ({ commit }: ActionContext<State, State>, type: string = 'standard') => {
    const url = `${ process.env.VUE_APP_WAIVER_COPY_URL }/${ type }`;
    try {
      const { paragraphs, agreement } = await axios.get(url).then(({ data }) => data);
      commit(UPDATE_WAIVER_TYPE, type);
      commit(UPDATE_WAIVER_CONTRACT, paragraphs);
      return { paragraphs, agreement };
    } catch (err) {
      console.error(`Failed to fetch waiver copy for type "${ type }":`, err);
    }
  },

  [CREATE_MINORS]: async ({ commit, state }: ActionContext<State, State>, { minors, guardian, reservationId }: any) => {
    const rId = reservationId ?? state[SELECTED_RESERVATION]?.id
    if (!rId) console.error(`No reservation id could be found`);

    const gId = guardian?.id ?? state[PERSON]?.id
    if (!gId) console.error(`No guardian id could be found`)

    const toSubmit = minors ?? state[MINORS];

    const createdMinors = await findOrCreateMinors(toSubmit, gId)

    commit(UPDATE_MINORS, createdMinors)
    return createdMinors
  },

  [FETCH_BOOKER]: async ({ commit }: ActionContext<State, State>, { id }: Record<'id', string>) => {
    const url = `${ process.env.VUE_APP_BOOKERS }/waiver/${ id }`
    const foundBooker = await axios.get(url).then(({ data }) => data)
    if (foundBooker) {
      commit(UPDATE_BOOKER, foundBooker)
      return foundBooker
    } else {
      const err = `No booker could be found for id: ${ id }`
      console.error(err)
      throw new Error(err)
    }
  },

  [FETCH_GLOBAL_DATA]: async ({ commit, state }: any) => {
    // fetch survey 
    const [ surveyData, tagsData ] = await Promise.all([
      fetchSurveys(state[SURVEY_ID]),
      fetchTags()
    ])

    // TODO: implement better error handling
    if (surveyData) commit(UPDATE_SURVEYS, surveyData);
    if (tagsData) commit(UPDATE_TAGS, tagsData);
  },

  // TODO: rename to be more accurate about it's usage
  [FETCH_PEOPLE]: async ({ commit }: any, { email, phone }: Record<string, any>) => {
    const foundPeople = await axios.get(`${ process.env.VUE_APP_PEOPLE }/search/email/${ email }/phone/${ phone }`).then(({ data }) => data);
    return foundPeople;
  },

  [FETCH_PERSON]: async ({ commit, state }: any, id: number) => {
    if (state[PERSON]?.id === id) {
      console.warn(`Preventing another request, person with id: ${ id } already selected`);
      return;
    }
    
    if(process.env.VUE_APP_ONLINE_WAIVER == 1){
      const fetched_xola_id = state[SELECTED_RESERVATION]?.xola_order_id;
  
      if (!fetched_xola_id) {
        console.error('No xola_order_id found in state');
        return;
   }

      try {
        const response = await axios.get(`${process.env.VUE_APP_RESERVATIONS}/xola_order_id/${fetched_xola_id}`);
        const foundPerson = response.data.Booker.Person;
        commit(UPDATE_PERSON, foundPerson);
        return foundPerson;
      }catch (error) {
        console.log('incorrect endpoint used on action ts page');
        console.log(error);
    }
    }else{
      const foundPerson = await axios.get(`${ process.env.VUE_APP_PEOPLE }/${ id }`).then(({ data }) => data);
      if (!foundPerson) throw new Error(`Could not find person with id: ${ id }`);
      commit(UPDATE_PERSON, foundPerson);
      return foundPerson;
    }
  },

  [FETCH_RESERVATION]: async ({ commit, state }: any, { id }: Record<string, number>) => {

    try {
      const reservation = await axios.get(`${process.env.VUE_APP_RESERVATIONS}/xola_order_id/${id}`).then(({ data }) => data);
      
      if (!reservation) {
        console.error(`Could not find reservation with id: ${id}`);
        throw new Error(`Could not find reservation with id: ${id}`);
      }
      
      if (process.env.VUE_APP_ONLINE_WAIVER == 1) {
        const xola_order_id = reservation.xola_order_id; 
  
        if (!xola_order_id) {
          console.error('No xola_order_id found in the reservation response');
          return;  
        }
  
        commit(UPDATE_SELECTED_RESERVATION, { ...reservation, xola_order_id });
      } else {
        commit(UPDATE_SELECTED_RESERVATION, reservation);
      }
      console.log('Updated selected reservation:', state.SELECTED_RESERVATION);
      return reservation;
    } catch (err) {
      console.error('Error fetching reservation:', err);
      return null;
    }
  },

  [FETCH_RESERVATIONS_IN_RANGE]: async ({ commit }: any, { start, end }: Record<string, string>) => {
    try {
      const reservations = await axios.get(`${ process.env.VUE_APP_RESERVATIONS }/waiver/checkin/start/${ start }/end/${ end }`).then(({ data }) => data);
      if (!reservations.length) return
      else {
        commit(UPDATE_RESERVATIONS, reservations);
        return reservations;
      }
    } catch (err) {
      console.error(err);
      throw err
    }
  },

  [FIND_OR_CREATE_PERSON]: async ({ commit }: ActionContext<State, State>, personDetails: Person) => {
    const foundOrCreated = await findOrCreatePerson(personDetails).then(({ data }) => data)

    if (foundOrCreated) {
      commit(UPDATE_PERSON, foundOrCreated)
      return foundOrCreated
    }
  },

  [FIND_OR_CREATE_RESERVATION]: async ({ commit, state }: ActionContext<State, State>, reservationDetails: any) => {
    // TODO: figure out if there is another way we should be retrieving the reservation data when user cilcks "need help"
    const { booker_id, final_dollar_amount, location_id, mission_id, size, xola_order_id, xola_item_id } = reservationDetails ?? state[SELECTED_RESERVATION]
    
    const fetchedReservation = axios.post(`${ process.env.VUE_APP_RESERVATIONS }/find_or_create/xola_order_id/${ xola_order_id }/xola_item_id/${ xola_item_id }`, {
      xola_order_id,
      size,
      booker_id,
      final_dollar_amount,
      location_id,
      mission_id
    }).then(({ data }) => data)

    if (!fetchedReservation) throw new Error(`No reservation could be found or created`)
    commit(UPDATE_SELECTED_RESERVATION, fetchedReservation)
    return fetchedReservation
  },


  [SUBMIT_KLAVIYO]: async ({ state, getters }: ActionContext<State, State>, person: any) => {
    const isBooker = getters[IS_BOOKER];
    if (isBooker){
      try {
        await axios.post(
          `${process.env.VUE_APP_KLAVIYO}/api/booker-profile/${state[BOOKER]?.id}`,
          {}
        );
      } catch (error) {
        if (error instanceof Error) {
          console.error(`Failed to submit booker profile: ${error.message}`);
        } else {
          console.error('Unknown error occurred while submitting booker profile');
        }
      }

      try {
        await axios.post(
          `${process.env.VUE_APP_KLAVIYO}/api/booker-subscription/${state[BOOKER]?.id}`,
          {}
        );
      } catch (error) {
        if (error instanceof Error) {
          console.error(`Failed to submit booker subscription: ${error.message}`);
        } else {
          console.error('Unknown error occurred while submitting booker subscription');
        }
      }

      return;
    }

    const profile = person ?? state[PERSON];
    const smsOptin = state[SMS_OPTIN];
  
    const { $consent, sms_consent, phone_number, email, ...profileData } = formatKlaviyoProfile({
      ...profile,
      smsOptin
    });
  
    const addProfile = { $consent, sms_consent, phone_number, email, ...profileData };
  
    try {
      await postProfileToKlaviyo(
        `${process.env.VUE_APP_KLAVIYO}/api/profile-import`,
        [addProfile] 
      );
      console.log(`Profile added to Klaviyo`);
    } catch (error) {
      if (error instanceof Error) {
        console.error(`Failed to submit profile: ${error.message}`);
      } else {
        console.error('Unknown error occurred while adding profile');
      }
    }
    
    const subscribeProfile = { sms_consent, phone_number, email, ...($consent.includes('sms') && { $consent: ['sms'] }) };

    try {
        await postToKlaviyoSubscriptionEmailOnly(
          `${process.env.VUE_APP_KLAVIYO}/api/profile-subscription-bulk-create-jobs`,
          [subscribeProfile],
          process.env.VUE_APP_KLAVIYO_ADD_LIST_ID 
        );
        console.log(`Player subscribed to Klaviyo list (email only): ${process.env.VUE_APP_KLAVIYO_ADD_LIST_ID}`);
      } catch (error) {
        if (error instanceof Error) {
          console.error(`Failed to subscribe player: ${error.message}`);
        } else {
          console.error('Unknown error occurred while subscribing player');
        }
      }
  
    if (smsOptin.marketing_optin) {
      const subscribeProfile = { sms_consent, phone_number, email, ...($consent.includes('sms') && { $consent: ['sms'] }) };
  
      try {
        await postToKlaviyoSubscriptionEmailAndSMS(
          `${process.env.VUE_APP_KLAVIYO}/api/profile-subscription-bulk-create-jobs`,
          [subscribeProfile],
          process.env.VUE_APP_KLAVIYO_SUBSCRIBE_LIST_ID 
        );
        console.log(`Player subscribed to Klaviyo list (email AND SMS): ${process.env.VUE_APP_KLAVIYO_SUBSCRIBE_LIST_ID}`);
      } catch (error) {
        if (error instanceof Error) {
          console.error(`Failed to subscribe player: ${error.message}`);
        } else {
          console.error('Unknown error occurred while subscribing player');
        }
      }
    }
  },
   
  
  [SUBMIT_WAIVER]: async ({ dispatch, getters, state }: ActionContext<State, State>) => {

    console.log('submit waiver function trying to print');

    const booker = state[BOOKER]
    const guardian = state[GUARDIAN]
    const heardFrom = state[HEARD_FROM]
    const isPlayer = state[PLAYER]
    const minors = state[MINORS]
    const occasion = state[OCCASION]
    const person = state[PERSON]
    const reservation = state[SELECTED_RESERVATION]
    const signature = state[SIGNATURE]
    const smsOptin = state[SMS_OPTIN];
    const survey_id = state[SURVEY_ID]
    const survey_answers = state[SURVEY_ANSWERS]
    const surveyData = getters[GET_SURVEY_OPTIONS][survey_id]
    const online_waiver = process.env.VUE_APP_ONLINE_WAIVER
    const waiver_type = state[WAIVER_TYPE]
    const isBooker = getters[IS_BOOKER]


    

    console.log(`Is this an online waiver? ${online_waiver}`);

    const arrived = online_waiver != 0 ? 0 : 1
    console.log(`Have we arrived? ${arrived}`);
    const marketing_consent = smsOptin.marketing_optin ? 1 : 0

    try {
      var person_id : number;
      
      if (isBooker) {
        person_id = person?.id || 0;
        await updatePersonById(person_id, { ...person, marketing_consent })
      }
      else {
        const [storedPerson, personCreated] = await findOrCreatePerson({ ...person, marketing_consent }).then(({ data }) => data)
        person_id = storedPerson.id;
      }

      // TODO: update person if not created?
      const reservationPersonDetails = {
        arrived,
        non_player: isPlayer === 1 ? 0 : 1,
        person_id: person_id,
        reservation_id: reservation?.id
      } as ReservationPerson
      await findOrCreateReservationPerson({ ...reservationPersonDetails })
      const [storedPlayer] = await findOrCreatePlayer(person_id).then(({ data }) => data)
      guardian && await storeMinors(minors, storedPlayer?.id, reservation?.id, arrived)
      if (booker?.Person?.id === person_id) await createReservationTag({ other: occasion?.detail, reservation_id: reservation?.id, tag_id: occasion?.id! })
      
      // NOTE: Leaving this in here.
      if (heardFrom){
        await createPersonSurveyResponse(
          { 
            answer_id: heardFrom?.id!, 
            open_text: heardFrom?.detail, 
            person_id: person_id, 
            question_id: surveyData.question_id, 
            survey_id 
          }
        )
      }
      
      if (survey_answers){
        await submitPersonSurveyResponses(survey_answers, person_id);
      }

      createWaiverForPerson(person_id, { person: person!, minors, signature: signature!, online_waiver, waiver_type })
      dispatch(SUBMIT_KLAVIYO, state[PERSON])
      
      return true;
    } catch (err) {
      console.error(err)
      return false
    }
  }
};

const storeMinors = async (minors: WaiverMinor[], guardianId: number, reservation_id: number, arrived: IntBoolean) => {

  console.log('stored minors function ');

  const storedMinors = []
  for (const m of minors) {


    /** added if clause to skip posting the minor data which was null **/
    if(m.first_name == null || m.last_name == null || m.date_of_birth == null){
      continue;
    }

    const storedMinor = await findOrCreateMinor({ ...m, player_id: guardianId }).then(({ data }) => data)
    const storedReservationMinor = await findOrCreateReservationMinor({ 
      player_minor_id: storedMinor?.id,
      reservation_id,
      arrived,
      non_player: m.isPlayer ? 0 : 1
    })

    storedMinors.push([storedMinor, storedReservationMinor])
  }
  
  return storedMinors
}

const findOrCreateMinors = async (minors: PlayerMinor[], guardianId: number) => {

  console.log('inside find or create minors');

  return Promise.all(minors.map(({ first_name, last_name, date_of_birth }: any) => findOrCreateMinor({
    first_name,
    last_name,
    date_of_birth,
    player_id: guardianId
  }).then(({ data }) => data)
))
}

const generateWaiverPDF = async ({ first_name, last_name, email, date_of_birth }: Partial<Person>, minors: PlayerMinor[], signature: string, online_waiver: IntBoolean, person_id: number, waiver_type: string = 'standard') => {
  const url = `${ process.env.VUE_APP_PDF_WAIVER_URL }`;
  var minorObject;

  console.log(`Hey hey so there's an ID and it's ${person_id}`);

  console.log('inside generate waiver pdf');
  if(minors == undefined || minors.length == 0){
    console.log('no minors');

    minorObject = [{
        "first_name": "",
         "last_name": "",
         "date_of_birth": "",
         "minorPlayerOrNot": "1"
    }]
  }
  else{
    minorObject = minors;
  }
  if(minors == null){
    console.log('empty minor san');
  }

  return axios.post(url, {
    first_name,
    last_name,
    email,
    date_of_birth,
    minorsDetail: minorObject,
    signature,
    online_waiver,
    person_id,
    waiver_type
  })
}

type CreateWaiver = {
  waiver_url: string
  online_waiver?: IntBoolean
}

const createWaiverEntry = async (body: CreateWaiver) => {
  const url = `${ process.env.VUE_APP_WAIVERS }`
  return axios.post(url, { ...body })
}

const createWaiverPDF = async ({ person, minors, signature, online_waiver, waiver_type = 'standard' }: CreateWaiverPDF, person_id: number) => {
  const { Location } = await generateWaiverPDF(person, minors, signature, online_waiver, person_id, waiver_type).then(({ data }) => data)
  return Location
}

type CreateWaiverPDF = {
  person: Partial<Person>,
  minors: PlayerMinor[],
  signature: string,
  online_waiver: IntBoolean,
  waiver_type?: string
}

const createWaiverForPerson = async (id: number, { person, minors, signature, online_waiver, waiver_type }: CreateWaiverPDF) => {
  const waiver_response = await createWaiverPDF({ person, minors, signature, online_waiver, waiver_type }, id)
  return waiver_response;
}

const submitPersonSurveyResponses = async (surveyAnswers: Survey[], personId: number) => {
  await Promise.all(surveyAnswers.map(async (surveyAnswer) => {
    console.log(`SurveyAnswer is : ${JSON.stringify(surveyAnswer)}`);
    surveyAnswer.person_id = personId;
    await createPersonSurveyResponse(surveyAnswer);
  }));
}

const createPersonSurveyResponse = async (personSurvey: Survey) => {
  const url = `${ process.env.VUE_APP_PERSON_SURVEY_ANSWER }`
  return axios.post(url, { ...personSurvey })
}

// TODO: determine if there is a findOrCreate route
const findOrCreateMinor = async (minor: Partial<PlayerMinor>) => {
  const url = `${ process.env.VUE_APP_PLAYERMINOR }`
  return axios.post(url, {
    ...minor
  })
}

const createReservationTag = async ({ other, reservation_id, tag_id }: ReservationTag) => {
  const url = `${ process.env.VUE_APP_RESERVATION_TAGS }`
  return axios.post(url, {
    other,
    reservation_id,
    tag_id
  })
}

const findOrCreatePerson = async ({ email, first_name, last_name, date_of_birth, phone, instagram, gender_id, postal_code, marketing_consent }: Partial<WaiverPerson>) => {
  
  console.log(email, first_name, last_name, date_of_birth, phone, instagram, gender_id, postal_code, marketing_consent);

  console.log(process.env.VUE_APP_PEOPLE+'/find_or_create/email/'+email+'/first_name/'+first_name+'/last_name/'+last_name);

  console.log('san version 1.1');

  const url = `${ process.env.VUE_APP_PEOPLE }/find_or_create/email/${ email }/first_name/${ first_name }/last_name/${ last_name }`
  return axios.post(url, {
    date_of_birth,
    email,
    first_name,
    gender_id,
    instagram,
    last_name,
    phone,
    postal_code,
    marketing_consent
  })
}

const findOrCreatePlayer = async (person_id: number) => {
  const url = `${ process.env.VUE_APP_PLAYERS }/find_or_create/${ person_id }`
  return axios.post(url, {})
}

const findOrCreateReservationPerson = async ({ arrived, non_player, person_id, reservation_id }: Partial<ReservationPerson>) => {
  const url = `${ process.env.VUE_APP_RESERVATIONPEOPLE }/find_or_create/person/${ person_id }/reservation/${ reservation_id }`
  return axios.post(url, {
    arrived,
    non_player,
    person_id,
    reservation_id,
  })
}

const findOrCreateReservationMinor = async ({ player_minor_id, reservation_id, arrived, non_player }: Partial<ReservationMinor>) => {
  const url = `${ process.env.VUE_APP_RESERVATION_MINOR }/find_or_create/player_minor/${ player_minor_id }/reservation/${reservation_id}`
  return axios.post(url, {
    arrived,
    non_player,
    player_minor_id,
    reservation_id,
  })
}

const fetchSurveys = async (id: number): Promise<Record<string, any>> => {
  const surveys = await axios.get(`${ process.env.VUE_APP_SURVEY }/${ id }`).then(({ data }) => data);
  if (surveys) return surveys;
  else throw new Error(`Could not find survey for id: ${ id }`);
}

const fetchTags = async () => {
  const tags = axios.get(`${ process.env.VUE_APP_TAGS }`).then(({ data }) => data);
  if (tags) return tags;
  else throw new Error('Could not fetch any tags');
}

const formatKlaviyoProfile = (personData: any) => {
  const { first_name, last_name, phone, email, smsOptin } = personData
  const phoneFixed = phone ? formatPhone(`${ phone }`, { country: 'USA' }) : phone;
  const $consent = ['email']

  if (smsOptin.marketing_optin) $consent.push('sms');

  return {
    first_name: first_name,
    last_name: last_name,
    phone_number: phoneFixed?.phoneNumber ?? phone,
    email,
    marketing_consent: smsOptin.marketing_optin ? "true" :"false",
    $consent,
    sms_consent: smsOptin.marketing_optin
  }
}

const postProfileToKlaviyo = async (url: string, profiles: any[]) => {
  const profile = profiles[0]; 

  const { subscriptions, ...profileWithoutSubscriptions } = profile;

  const body = {
    data: {
      attributes: {
        phone_number: profileWithoutSubscriptions.phone_number || null,
        email: profileWithoutSubscriptions.email || null,
        first_name: profileWithoutSubscriptions.first_name || null,
        last_name: profileWithoutSubscriptions.last_name || null,
        properties: {
          PhoneNumber: profileWithoutSubscriptions.phone_number || null,
          $phone: profileWithoutSubscriptions.phone_number || null,
          ...profileWithoutSubscriptions.properties,
        }
      }
    }
  };

  return axios.post(url, body, {
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    timeout: 10000
  });
};

const postToKlaviyoSubscriptionEmailOnly = async (url: string, profiles: any[], listId: string) => {
  const formattedProfiles = profiles.map(profile => ({
    attributes: {
      phone_number: profile.phone_number || null,
      email: profile.email || null,
      properties: {
        PhoneNumber: profile.phone_number || null,
        $phone: profile.phone_number || null,
        ...profile.properties
      },
      subscriptions: {
        email: {
          marketing: {
            consent: "SUBSCRIBED"
          }
        }
      }
    }
  }));

  const body = {
    data: {
      attributes: {
        profiles: {
          data: formattedProfiles
        },
        historical_import: false
      },
      relationships: {
        list: {
          data: {
            type: "list",
            id: listId 
          }
        }
      }
    }
  };

  return axios.post(url, body, {
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    timeout: 10000
  });
};

const postToKlaviyoSubscriptionEmailAndSMS = async (url: string, profiles: any[], listId: string) => {
  const formattedProfiles = profiles.map(profile => ({
    attributes: {
      phone_number: profile.phone_number || null,
      email: profile.email || null,
      properties: {
        PhoneNumber: profile.phone_number || null,
        $phone: profile.phone_number || null,
        ...profile.properties
      },
      subscriptions: {
        email: {
          marketing: {
            consent: "SUBSCRIBED"
          }
        },
        sms: {
          marketing: {
            consent: "SUBSCRIBED"
          },
          transactional: {
            consent: "SUBSCRIBED"
          }
        }
      }
    }
  }));

  const body = {
    data: {
      attributes: {
        profiles: {
          data: formattedProfiles
        },
        historical_import: false
      },
      relationships: {
        list: {
          data: {
            type: "list",
            id: listId 
          }
        }
      }
    }
  };

  return axios.post(url, body, {
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    timeout: 10000
  });
};


const updatePersonById = async (id: number, personDetails: Partial<WaiverPerson>) => {
  const url = `${ process.env.VUE_APP_PEOPLE }/${ id }`
  return axios.put(url, {
    ...personDetails
  })
}

const updateReservationPersonById = async (id: number, personDetails: Partial<WaiverPerson>) => {
  const url = `${ process.env.VUE_APP_RESERVATIONPEOPLE }/${ id }`
  return axios.put(url, {
    ...personDetails
  })
}